import * as React from "react";
import { useTranslation } from "react-i18next";
import { TextField } from "./TextField";
import { AvatarUploadField } from "./AvatarUploadField";
import { Select } from "./Select";
import { Textarea } from "./Textarea";
import { StarRatingInput } from "./StarRatingInput";
import { Field } from "./Field";
import { Button } from "./Button";
import { FormError } from "./FormError";
import { Icon } from "./Icon";
import { SERVICES } from "../../api/reviews";
import type { ReviewService } from "../../api/reviews";
import { useSubmitReviewMutation } from "../../hooks/useReviews";

type Props = {
  /** Fired once the review is accepted — lets the host modal swap its header. */
  onSuccess?: () => void;
  /** Closes the host modal from the confirmation's "Done" action. */
  onClose?: () => void;
};

const SELECTABLE_SERVICES = SERVICES.filter((s) => s.id !== "ALL");

export function ReviewForm({ onSuccess, onClose }: Props = {}) {
  const { t } = useTranslation("reviews");
  const serviceOptions = React.useMemo(
    () =>
      SELECTABLE_SERVICES.map((s) => ({ label: t(s.labelKey), value: s.id })),
    [t]
  );
  const mutation = useSubmitReviewMutation();
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [service, setService] = React.useState<ReviewService>(
    SELECTABLE_SERVICES[0].id as ReviewService
  );
  const [title, setTitle] = React.useState("");
  const [rating, setRating] = React.useState(0);
  const [body, setBody] = React.useState("");
  const [avatar, setAvatar] = React.useState<File | null>(null);
  const [showErrors, setShowErrors] = React.useState(false);

  const errors = {
    name: name.trim().length > 1 ? undefined : t("nameRequired"),
    email:
      email.trim() === "" || /^\S+@\S+\.\S+$/.test(email.trim())
        ? undefined
        : t("emailInvalid"),
    title: title.trim().length > 1 ? undefined : t("headlineRequired"),
    rating: rating > 0 ? undefined : t("ratingRequired"),
    body:
      body.trim().length >= 12
        ? undefined
        : t("bodyTooShort"),
  };
  const valid =
    !errors.name &&
    !errors.email &&
    !errors.title &&
    !errors.rating &&
    !errors.body;

  React.useEffect(() => {
    if (mutation.isSuccess) onSuccess?.();
  }, [mutation.isSuccess, onSuccess]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mutation.isPending) return;
    if (!valid) {
      setShowErrors(true);
      return;
    }
    mutation.mutate({
      name: name.trim(),
      service,
      title: title.trim(),
      body: body.trim(),
      rating,
      ...(email.trim() ? { email: email.trim() } : {}),
      ...(avatar ? { avatar } : {}),
    });
  };

  if (mutation.isSuccess) {
    // Headerless modal: this card is self-contained — mark, title, status, exit.
    return (
      <div className="flex flex-col items-center gap-5 py-6 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-[22px] text-primary">
          <Icon name="verified" title="Review submitted" />
        </span>
        <p className="t-display text-primary">{t("thankYouTitle")}</p>
        <p className="t-body-blue max-w-[32ch] text-primary/70">
          {t("thankYouBody")}
        </p>
        <Button
          variant="outline"
          surface="light"
          as="button"
          type="button"
          onClick={onClose}
        >
          {t("done")}
        </Button>
      </div>
    );
  }

  return (
    // Fill the modal body and cancel its padding so the fields own the scroll
    // region (flex-1) and the action stays a fixed footer (flex-none).
    // iOS Safari pins this inner scroller to its end on contact AutoFill —
    // Modal's capture-phase autofill guard puts the scroll back (this layout
    // relies on it; don't strip the guard).
    <form
      onSubmit={handleSubmit}
      className="-mx-10 -mb-8 -mt-7 flex min-h-0 flex-1 flex-col"
      noValidate
    >
      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-10 pb-7 pt-7">
        <TextField
          label={t("nameLabel")}
          requirement="required"
          value={name}
          onChange={setName}
          placeholder={t("namePlaceholder")}
          name="name"
          autoComplete="name"
          error={showErrors ? errors.name : undefined}
        />
        <TextField
          label={t("emailLabel")}
          requirement="optional"
          type="email"
          value={email}
          onChange={setEmail}
          placeholder={t("emailPlaceholder")}
          name="email"
          autoComplete="email"
          hint={t("emailHint")}
          error={showErrors ? errors.email : undefined}
        />
        <AvatarUploadField
          label="Your photo"
          requirement="optional"
          value={avatar}
          onChange={setAvatar}
          hint={t("photoHint")}
        />
        <Select
          label="Service"
          requirement="required"
          value={service}
          onChange={(v) => setService(v as ReviewService)}
          options={serviceOptions}
          name="service"
        />
        <TextField
          label={t("headlineLabel")}
          requirement="required"
          value={title}
          onChange={setTitle}
          placeholder={t("headlinePlaceholder")}
          // iOS Safari reads name="title" as a contact job-title and group-fills
          // it on name autofill, scrolling the form to the end — keep this out
          // of the contact-autofill group.
          name="review-title"
          autoComplete="off"
          error={showErrors ? errors.title : undefined}
        />
        <Field
          label="Rating"
          requirement="required"
          error={showErrors ? errors.rating : undefined}
        >
          <StarRatingInput value={rating} onChange={setRating} readout />
        </Field>
        <Textarea
          label={t("bodyLabel")}
          requirement="required"
          value={body}
          onChange={setBody}
          maxLength={1000}
          placeholder={t("bodyPlaceholder")}
          hint={t("bodyHint")}
          name="body"
          error={showErrors ? errors.body : undefined}
        />
        {mutation.isError && <FormError message={t("sendError")} />}
      </div>
      <div className="flex-none border-t border-primary/15 bg-white px-10 py-6 [&>button]:w-full">
        <Button
          variant="solid"
          surface="light"
          as="button"
          type="submit"
          disabled={mutation.isPending}
        >
          {t("submit")}
        </Button>
      </div>
    </form>
  );
}
